import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { createRole, updateRole } from "../services/roleService";
import { humanise } from "../services/enumLabels";

/**
 * Create or edit one role. `role` null means create.
 *
 * The tick-box matrix is built from the catalogue the API sends, so a
 * permission added on the server shows up here without a portal change. What is
 * ticked replaces the role's whole set on save.
 *
 * `implies` is the server's rule about what one permission grants on its own —
 * a write carries its module's read, because a role that may delete clients
 * has to be able to see the list it deletes from. Those reads show ticked and
 * locked, and are *not* added to what is saved: the server resolves them on
 * every check, so storing a row for them would only duplicate the rule in a
 * place that can fall out of step with it. Unticking the write is what takes
 * the read away, which is why the box is locked while the write is on.
 *
 * A system role keeps its name — code and this portal find those four by slug —
 * but its permissions stay editable, which is the part a business cares about.
 */
export default function RoleModal({ role, catalogue, implies, open, onClose, onSaved }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [selected, setSelected] = useState([]);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [saving, setSaving] = useState(false);

  const editing = Boolean(role);
  const isSystem = Boolean(role?.is_system);

  useEffect(() => {
    if (!open) return;

    setName(role?.name ?? "");
    setSelected(role?.permissions ?? []);
    setErrors({});
    setBanner("");
  }, [open, role]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, saving]);

  const modules = useMemo(() => Object.entries(catalogue ?? {}), [catalogue]);

  /** The reads the current selection grants without being ticked for. */
  const implied = useMemo(() => {
    const derived = new Set();

    selected.forEach((permission) =>
      (implies?.[permission] ?? []).forEach((read) => derived.add(read))
    );

    return derived;
  }, [selected, implies]);

  /** Ticked, whether it was chosen or comes with something that was. */
  const isOn = (permission) => selected.includes(permission) || implied.has(permission);

  const toggle = (permission) =>
    setSelected((current) =>
      current.includes(permission)
        ? current.filter((p) => p !== permission)
        : [...current, permission]
    );

  /** Tick or clear a whole module at once — the common case when setting a role up. */
  const toggleModule = (permissions) => {
    const allOn = permissions.every(isOn);

    setSelected((current) =>
      allOn
        ? current.filter((p) => !permissions.includes(p))
        : [...new Set([...current, ...permissions])]
    );
  };

  /**
   * What gets saved never repeats what is implied. The server resolves the
   * read from the write on every check, so a row for it would be a second
   * copy of the same decision — one that would outlive the write it came
   * from. A read ticked on its own, with no write behind it, is a real choice
   * and is stored.
   */
  const toSave = () => selected.filter((permission) => !implied.has(permission));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    setBanner("");

    try {
      const permissions = toSave();

      const saved = editing
        ? await updateRole(role.id, { name, permissions })
        : await createRole({ name, permissions });

      onSaved(saved, editing);
    } catch (err) {
      setErrors(apiFieldErrors(err));
      setBanner(apiErrorMessage(err, t("roles.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const fieldError = (field) => errors[field]?.[0];

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
      <div role="dialog" aria-modal="true" className="w-full max-w-3xl rounded-xl bg-surface shadow-xl">
        <form onSubmit={submit} className="flex max-h-[85vh] flex-col">
          <div className="flex items-center justify-between border-b border-line px-5 py-4">
            <h2 className="text-lg font-semibold">
              {editing ? t("roles.edit", { name: role.name }) : t("roles.new")}
            </h2>
            <button type="button" onClick={onClose} className="text-sm text-ink-2 hover:text-ink">
              {t("common.close")}
            </button>
          </div>

          <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
            {banner && (
              <div className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
                {banner}
              </div>
            )}

            <div>
              <label className="field-label" htmlFor="role-name">
                {t("roles.form.name")}
              </label>
              <input
                id="role-name"
                type="text"
                value={name}
                disabled={isSystem}
                onChange={(e) => setName(e.target.value)}
                className="field-input disabled:bg-raised disabled:text-ink-2"
              />
              {isSystem && <p className="mt-1 text-xs text-ink-2">{t("roles.form.systemName")}</p>}
              {fieldError("name") && <p className="field-error">{fieldError("name")}</p>}
            </div>

            <div>
              <p className="field-label">{t("roles.form.permissions")}</p>
              {fieldError("permissions") && <p className="field-error">{fieldError("permissions")}</p>}

              <div className="mt-2 space-y-3">
                {modules.map(([module, permissions]) => {
                  const allOn = permissions.every(isOn);

                  return (
                    <div key={module} className="rounded-lg border border-line">
                      <div className="flex items-center justify-between border-b border-line bg-raised px-3 py-2">
                        <span className="text-sm font-semibold">
                          {t(`permissionModules.${module}`, { defaultValue: humanise(module) })}
                        </span>
                        <button
                          type="button"
                          onClick={() => toggleModule(permissions)}
                          className="text-xs font-semibold text-accent-text hover:underline"
                        >
                          {allOn ? t("roles.form.clearAll") : t("roles.form.selectAll")}
                        </button>
                      </div>

                      <div className="grid gap-2 px-3 py-3 sm:grid-cols-2 lg:grid-cols-3">
                        {permissions.map((permission) => {
                          // Granted by something else that is ticked: shown on,
                          // and not for turning off on its own.
                          const derived = implied.has(permission);

                          return (
                            <label key={permission} className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={isOn(permission)}
                                disabled={derived}
                                onChange={() => toggle(permission)}
                                className="h-4 w-4 rounded border-line-strong text-accent-text disabled:opacity-60"
                              />
                              <span className={derived ? "text-ink-2" : undefined}>
                                {t(`permissionActions.${permission.split(".").slice(1).join(".")}`,
                                  permission.split(".").slice(1).join("."))}
                              </span>
                              {derived && (
                                <span
                                  title={t("roles.form.impliedHint")}
                                  className="rounded bg-raised px-1.5 py-0.5 text-xs text-ink-2"
                                >
                                  {t("roles.form.implied")}
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-4">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? t("roles.form.saving") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
